import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {AuthUser} from '../models/auth-user';
import {Router} from '@angular/router';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {LoginUser} from '../../login/models/loginUser';
import {SessionRecord, StorageService} from '../../shared/services/storage.service';
import {ModuleSchemaStoreService} from '../../services/module-schema-store.service';
import {FormSchemaStoreService} from '../../services/form-schema-store.service';
import {expiresAtFromAccessToken} from './jwt-payload';

@Injectable()
export class AuthService {

  public authUser: AuthUser;
  private _user = new BehaviorSubject<AuthUser>(null);
  /** Coalesce parallel 401→logout storms (AC-24). */
  private unauthorizedLogoutInFlight = false;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private storage: StorageService,
    private moduleSchemaStore: ModuleSchemaStoreService,
    private formSchemaStore: FormSchemaStoreService,
  ) {
    this.authUser = this.getUser();
    this._user.next(Object.assign({}, this.authUser));
  }

  get user(): Observable<AuthUser> {
    return this._user.asObservable();
  }

  login(loginUser: LoginUser): Observable<AuthUser> {
    const url = `/auth/login`;
    return this.httpClient.post(url, loginUser)
      .pipe(
        map((response: any) => {
          return this.setToken(response)
        }))
  }

  changePassword(currentPassword: string, newPassword: string): Observable<AuthUser> {
    return this.httpClient.post('/auth/change-password', {currentPassword, newPassword})
      .pipe(map((response: any) => this.setToken(response)));
  }

  setToken(response) {
    this.unauthorizedLogoutInFlight = false;
    const user = response.profile || {};
    user.token = response.accessToken;
    user.isAuthenticated = true;
    user.isCustomer = user.rank === -1;
    this._user.next(user);
    this.saveLocalUser(user);
    return user;
  }

  googleLogin(): void {
    const url = `auth/google`;
    const port = window.location.port === '4300' ? '3000' : window.location.port

    // @ts-ignore
    window.location = `${window.location.protocol}//${window.location.hostname}:${port}/api/${url}`;
  }

  logout() {
    // Keep baseUrl so the next login still targets the tenant API.
    this.clearCredentials();
    this.router.navigate(['login']);
  }

  /**
   * Single coordinated 401 logout (AC-24). Returns false if a logout is already in flight
   * so interceptors / bootstrap validation do not storm navigate/clear.
   */
  handleUnauthorized(returnUrl?: string): boolean {
    if (this.unauthorizedLogoutInFlight) {
      return false;
    }
    this.unauthorizedLogoutInFlight = true;
    // Keep tenant baseUrl/domain so re-login can still hit the same API host (AC-22/28).
    this.clearCredentials();
    this.router.navigate(['login'], returnUrl ? {queryParams: {returnUrl}} : undefined);
    return true;
  }

  /** Drop credentials + schema caches; keep tenant API host for re-login. */
  clearCredentials() {
    this.storage.clearSession();
    this.authUser = null;
    this._user.next(null);
    this.moduleSchemaStore.resetSession();
    this.formSchemaStore.resetSession();
  }

  /** Full session reset: credentials, tenant URL, and schema stores (explicit tenant wipe). */
  clearLocalData() {
    this.clearCredentials();
    this.storage.removeItem('baseUrl');
    this.storage.removeItem('domain');
  }

  getToken(): string {
    return this.getUser().token;
  }

  isAuthenticated() {
    return this.getUser().isAuthenticated;
  }

  hasClaim(claimType: string, claimValue?: string): boolean {
    return claimValue ? this.authUser[claimType] === claimValue : this.authUser[claimType];
  }

  hasUserRank(ranks: number[], n = ''): boolean {
    return ranks.includes(this.authUser?.rank) || ranks.includes(0);
  }

  isCustomer(): boolean {
    return this.authUser.rank < 0;
  }

  isAdmin(): Observable<boolean> {
    const url = `/auth/isAdmin`;
    return this.httpClient.get<boolean>(url);
  }
  getProfile(): Observable<AuthUser> {
    const url = `/auth/profile`;
    return this.httpClient.get<AuthUser>(url);
  }

  private saveLocalUser(user: AuthUser) {
    this.authUser = user;
    this._user.next(Object.assign({}, this.authUser));
    this.storage.setJson('currentUser', user);
    this.persistSessionMeta(user);
  }

  private persistSessionMeta(user: AuthUser) {
    const existing = this.storage.getSession() || {} as SessionRecord;
    // Bind credentials to the tenant API that issued them (current base URL).
    const issuer = this.getBaseUrl() || existing.issuer || '';
    const expiresAt = expiresAtFromAccessToken(user?.token) ?? existing.expiresAt ?? null;
    this.storage.setSession({
      ...existing,
      token: user?.token,
      issuer,
      expiresAt,
      schemaVersion: existing.schemaVersion ?? null,
      user: user as any,
    });
  }

  private getUser(): AuthUser {
    return this.storage.getJson<AuthUser>('currentUser', {} as AuthUser) || {} as AuthUser;
  }

  public getBaseUrl(): string {
    return this.storage.getItem('baseUrl');
  }

  public setBaseUrl(baseUrl: string): void {
    const current = this.getBaseUrl();
    if (current && baseUrl && !this.sameHost(current, baseUrl)) {
      // Company / tenant switch: drop prior credentials before pointing at a new API.
      this.invalidateCredentialsForTenantSwitch();
    }
    this.storage.setItem('baseUrl', baseUrl || '');
  }

  public setDomain(domain: string): void {
    const current = this.storage.getItem('domain');
    if (current && domain && current !== domain) {
      this.invalidateCredentialsForTenantSwitch();
    }
    this.storage.setItem('domain', domain);
  }

  /** Clear in-memory and persisted credentials without wiping the new tenant's baseUrl write. */
  invalidateCredentialsForTenantSwitch(): void {
    this.storage.clearSession();
    this.authUser = null;
    this._user.next(null);
    this.moduleSchemaStore.resetSession();
    this.formSchemaStore.resetSession();
  }

  private sameHost(a: string, b: string): boolean {
    try {
      const left = new URL(a, window.location.origin);
      const right = new URL(b, window.location.origin);
      const leftPort = left.port || (left.protocol === 'https:' ? '443' : '80');
      const rightPort = right.port || (right.protocol === 'https:' ? '443' : '80');
      return left.hostname.toLowerCase() === right.hostname.toLowerCase() && leftPort === rightPort;
    } catch {
      return a === b;
    }
  }

  public getDomain(): string {
    return this.storage.getItem('domain') || this.storage.getTenantId();
  }

  public getSchemaVersion(): string | number | null {
    const session = this.storage.getSession();
    return session?.schemaVersion ?? null;
  }

  public setSchemaVersion(version: string | number | null): void {
    const session = this.storage.getSession() || {} as SessionRecord;
    this.storage.setSession({...session, schemaVersion: version});
  }

  public getTokenIssuer(): string {
    return this.storage.getSession()?.issuer || this.getBaseUrl() || '';
  }
}
